import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2, Loader2, XCircle, PartyPopper, ArrowLeft } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";

export default function BookingSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { clear } = useCart();
  const [state, setState] = useState("checking"); // checking | success | pending | error
  const [booking, setBooking] = useState(null);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setState("error");
      return;
    }
    let attempts = 0;
    const poll = async () => {
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.data.payment_status === "paid") {
          setState("success");
          clear();
          if (res.data.booking_id) {
            const b = await api.get(`/bookings/${res.data.booking_id}`);
            setBooking(b.data);
          }
          return;
        }
        if (res.data.status === "expired" || attempts >= 6) {
          setState(attempts >= 6 ? "pending" : "error");
          return;
        }
        attempts += 1;
        setTimeout(poll, 2000);
      } catch {
        setState("error");
      }
    };
    poll();
    // eslint-disable-next-line
  }, []);

  return (
    <div className="min-h-screen grid place-items-center bg-cream noise-overlay px-5">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-lg rounded-[2rem] border border-black/10 bg-white p-8 md:p-10 text-center"
        data-testid="booking-success-card"
      >
        {state === "checking" && (
          <>
            <Loader2 size={56} className="mx-auto animate-spin text-brand-coral" />
            <h1 className="mt-5 font-display font-900 text-3xl">Confirming your deposit…</h1>
            <p className="mt-2 text-ink/60 font-500">Hang tight, this only takes a moment.</p>
          </>
        )}

        {state === "success" && (
          <>
            <div className="mx-auto grid place-items-center h-16 w-16 rounded-full bg-brand-green/15">
              <CheckCircle2 size={40} className="text-brand-green" />
            </div>
            <h1 className="mt-5 font-display font-900 text-3xl md:text-4xl">Booking confirmed! <PartyPopper className="inline text-brand-coral" /></h1>
            <p className="mt-3 text-ink/60 font-500">
              Your deposit is in. We've emailed the details and our crew will be in touch about delivery.
            </p>
            {booking && (
              <div className="mt-6 rounded-2xl bg-cream border border-black/10 p-5 text-left space-y-2" data-testid="success-summary">
                <Row label="Event date" value={booking.event_date} />
                <Row label="Items" value={booking.items.map((i) => `${i.name} ×${i.quantity}`).join(", ")} />
                <Row label="Deposit paid" value={`$${booking.deposit.toFixed(2)}`} highlight />
                <Row label="Balance on delivery" value={`$${(booking.subtotal - booking.deposit).toFixed(2)}`} />
              </div>
            )}
          </>
        )}

        {state === "pending" && (
          <>
            <Loader2 size={56} className="mx-auto text-brand-blue" />
            <h1 className="mt-5 font-display font-900 text-3xl">Payment processing</h1>
            <p className="mt-2 text-ink/60 font-500">It's taking a bit longer than usual. Check your email for confirmation.</p>
          </>
        )}

        {state === "error" && (
          <>
            <XCircle size={56} className="mx-auto text-brand-coral" />
            <h1 className="mt-5 font-display font-900 text-3xl">Hmm, something's off</h1>
            <p className="mt-2 text-ink/60 font-500">We couldn't confirm this payment. No charge may have been made.</p>
          </>
        )}

        <button
          onClick={() => navigate("/")}
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-ink text-white px-6 py-3 font-700 hover:bg-brand-coral transition-colors"
          data-testid="back-home-btn"
        >
          <ArrowLeft size={18} /> Back to home
        </button>
      </motion.div>
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div className="flex justify-between gap-4 text-sm">
      <span className="text-ink/50 font-500">{label}</span>
      <span className={`font-700 text-right ${highlight ? "text-brand-coral" : ""}`}>{value}</span>
    </div>
  );
}
